/**
 * Custom hook for managing pitch deck form state and database sync
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "@/hooks/use-toast";

export interface Form1Data {
  email: string;
  full_name: string;
  address: string;
  phone: string;
  company_name: string;
  problem_statement: string;
  solution_statement: string;
  team_members: string;
}

export interface Form2Data {
  product_description: string;
  market_size_local: string;
  market_size_regional: string;
  competitors: string;
  competitors_other: string; // Used when competitors === "other"
  differentiation: string;
  profit_margin: number | null;
  sales_projection: string;
}

export interface PitchDeckFormData extends Form1Data, Form2Data {
  id?: string;
  form_1_completed_at?: string | null;
  form_2_completed_at?: string | null;
}

const initialForm1Data: Form1Data = {
  email: "",
  full_name: "",
  address: "",
  phone: "",
  company_name: "",
  problem_statement: "",
  solution_statement: "",
  team_members: "",
};

const initialForm2Data: Form2Data = {
  product_description: "",
  market_size_local: "",
  market_size_regional: "",
  competitors: "",
  competitors_other: "",
  differentiation: "",
  profit_margin: null,
  sales_projection: "",
};

/**
 * Serializes only the user-editable fields, in a fixed key order, so the
 * "unsaved changes" baseline can be compared like-with-like.
 *
 * Previously the baseline was JSON.stringify() of the raw database row while
 * the comparison value was JSON.stringify() of the mapped UI model. Those
 * shapes can never match (different keys, different order), so the form was
 * permanently dirty and the 10-second autosave fired forever.
 *
 * Server-managed fields (id, *_completed_at) are excluded deliberately: they
 * change as a result of saving, not as a result of user edits.
 */
function serializeFormData(data: PitchDeckFormData): string {
  return JSON.stringify([
    data.email ?? "",
    data.full_name ?? "",
    data.address ?? "",
    data.phone ?? "",
    data.company_name ?? "",
    data.problem_statement ?? "",
    data.solution_statement ?? "",
    data.team_members ?? "",
    data.product_description ?? "",
    data.market_size_local ?? "",
    data.market_size_regional ?? "",
    data.competitors ?? "",
    data.competitors_other ?? "",
    data.differentiation ?? "",
    data.profit_margin ?? null,
    data.sales_projection ?? "",
  ]);
}

export function usePitchDeckForm() {
  const { user } = useAuth();
  const [formData, setFormData] = useState<PitchDeckFormData>({
    ...initialForm1Data,
    ...initialForm2Data,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof PitchDeckFormData, string>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [existingFormId, setExistingFormId] = useState<string | null>(null);
  
  const lastSavedDataRef = useRef<string>("");

  // Load existing form data
  useEffect(() => {
    async function loadExistingData() {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: existingForm, error } = await supabase
          .from("pitch_deck_forms")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          console.error("Error loading form data:", error);
          toast({
            title: "Error",
            description: "Failed to load your saved data",
            variant: "destructive",
          });
        }

        if (existingForm) {
          setExistingFormId(existingForm.id);
          
          // Parse competitors "Other: text" format
          let competitors = existingForm.competitors || "";
          let competitorsOther = "";
          if (competitors.startsWith("Other: ")) {
            competitorsOther = competitors.substring(7);
            competitors = "other";
          }
          
          const loadedFormData: PitchDeckFormData = {
            email: existingForm.email || "",
            full_name: existingForm.full_name || "",
            address: existingForm.address || "",
            phone: existingForm.phone || "",
            company_name: existingForm.company_name || "",
            problem_statement: existingForm.problem_statement || "",
            solution_statement: existingForm.solution_statement || "",
            team_members: existingForm.team_members || "",
            product_description: existingForm.product_description || "",
            market_size_local: existingForm.market_size_local || "",
            market_size_regional: existingForm.market_size_regional || "",
            competitors: competitors,
            competitors_other: competitorsOther,
            differentiation: existingForm.differentiation || "",
            profit_margin: existingForm.profit_margin,
            sales_projection: existingForm.sales_projection || "",
            id: existingForm.id,
            form_1_completed_at: existingForm.form_1_completed_at,
            form_2_completed_at: existingForm.form_2_completed_at,
          };

          setFormData(loadedFormData);
          // Baseline must be the SAME projection we compare against later.
          lastSavedDataRef.current = serializeFormData(loadedFormData);
        } else if (user.email) {
          // Pre-fill email from user auth
          setFormData((prev) => ({
            ...prev,
            email: user.email || "",
          }));
        }
      } catch (error) {
        console.error("Unexpected error loading form:", error);
      } finally {
        setIsLoading(false);
      }
    }

    loadExistingData();
  }, [user]);

  // Track unsaved changes
  useEffect(() => {
    const currentData = serializeFormData(formData);
    setHasUnsavedChanges(currentData !== lastSavedDataRef.current);
  }, [formData]);

  // Autosave every 10 seconds
  useEffect(() => {
    if (!user || isLoading) return;

    const autoSaveInterval = setInterval(() => {
      if (hasUnsavedChanges && !isSaving) {
        handleSave(false);
      }
    }, 10000);

    return () => clearInterval(autoSaveInterval);
  }, [hasUnsavedChanges, isSaving, user, isLoading]);

  const updateField = useCallback(<K extends keyof PitchDeckFormData>(
    field: K,
    value: PitchDeckFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }, [errors]);

  const handleSave = useCallback(async (showMessage = true, markForm1Complete = false, markForm2Complete = false) => {
    if (!user) {
      toast({
        title: "Error",
        description: "Please log in to save your form",
        variant: "destructive",
      });
      return false;
    }

    setIsSaving(true);

    // Snapshot what we are about to persist. If the user keeps typing while the
    // request is in flight, the form correctly stays dirty afterwards.
    const savedSnapshot = serializeFormData(formData);

    try {
      const now = new Date().toISOString();

      // Combine competitors with "other" text if applicable
      const competitorsValue = formData.competitors === "other"
        ? `Other: ${formData.competitors_other}`
        : formData.competitors;
      
      const baseData = {
        user_id: user.id,
        email: formData.email,
        full_name: formData.full_name,
        address: formData.address,
        phone: formData.phone,
        company_name: formData.company_name,
        problem_statement: formData.problem_statement,
        solution_statement: formData.solution_statement,
        team_members: formData.team_members,
        product_description: formData.product_description || null,
        market_size_local: formData.market_size_local || null,
        market_size_regional: formData.market_size_regional || null,
        competitors: competitorsValue || null,
        differentiation: formData.differentiation || null,
        profit_margin: formData.profit_margin,
        sales_projection: formData.sales_projection || null,
        updated_at: now,
      };

      let result;
      if (existingFormId) {
        const updateData = {
          ...baseData,
          ...(markForm1Complete ? { form_1_completed_at: now } : {}),
          ...(markForm2Complete ? { form_2_completed_at: now, completed_at: now } : {}),
        };
        
        result = await supabase
          .from("pitch_deck_forms")
          .update(updateData)
          .eq("id", existingFormId)
          .select()
          .single();
      } else {
        const insertData = {
          ...baseData,
          ...(markForm1Complete ? { form_1_completed_at: now } : {}),
          ...(markForm2Complete ? { form_2_completed_at: now, completed_at: now } : {}),
        };
        
        result = await supabase
          .from("pitch_deck_forms")
          .insert(insertData)
          .select()
          .single();
      }

      if (result.error) {
        throw result.error;
      }

      if (result.data) {
        setExistingFormId(result.data.id);
        setFormData((prev) => ({
          ...prev,
          id: result.data.id,
          form_1_completed_at: result.data.form_1_completed_at,
          form_2_completed_at: result.data.form_2_completed_at,
        }));
        lastSavedDataRef.current = savedSnapshot;
        setHasUnsavedChanges(false);
      }

      setLastSaved(new Date());

      if (showMessage) {
        toast({
          title: "Saved",
          description: "Draft saved successfully!",
        });
      }

      return true;
    } catch (error) {
      console.error("Error saving form:", error);
      if (showMessage) {
        toast({
          title: "Error",
          description: "Failed to save. Please try again.",
          variant: "destructive",
        });
      }
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [user, formData, existingFormId]);

  // Reset all pitch deck data (forms, generated decks, storage files)
  const handleReset = useCallback(async () => {
    if (!user) return false;

    try {
      // 1. Get all generated decks to find storage files
      const { data: decks } = await supabase
        .from("pitch_decks")
        .select("id, file_key")
        .eq("user_id", user.id);

      // 2. Delete storage files
      if (decks && decks.length > 0) {
        const filePaths = decks
          .map((d) => d.file_key)
          .filter((key): key is string => Boolean(key));
        if (filePaths.length > 0) {
          await supabase.storage.from("pitch-decks").remove(filePaths);
        }
      }

      // 3. Delete related records (order matters due to potential constraints)
      await supabase.from("pitch_deck_analytics").delete().eq("user_id", user.id);
      await supabase.from("pitch_deck_feedback").delete().eq("user_id", user.id);
      await supabase.from("pitch_decks").delete().eq("user_id", user.id);
      await supabase.from("pitch_deck_forms").delete().eq("user_id", user.id);

      // 4. Reset local state to initial values
      setFormData({
        ...initialForm1Data,
        ...initialForm2Data,
        email: user.email || "",
      });
      setExistingFormId(null);
      setErrors({});
      setLastSaved(null);
      setHasUnsavedChanges(false);
      lastSavedDataRef.current = "";

      return true;
    } catch (error) {
      console.error("Error resetting pitch deck data:", error);
      return false;
    }
  }, [user]);

  return {
    formData,
    setFormData,
    updateField,
    errors,
    setErrors,
    isSaving,
    isLoading,
    lastSaved,
    hasUnsavedChanges,
    handleSave,
    handleReset,
    isForm1Complete: !!formData.form_1_completed_at,
    isForm2Complete: !!formData.form_2_completed_at,
  };
}

// Validation helpers
export function countWords(text: string): number {
  return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
}

export function validateEmail(email: string): string | null {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!email.trim()) return "Email is required";
  if (!emailRegex.test(email)) return "Please enter a valid email address";
  return null;
}

export function validateTextLength(
  value: string,
  fieldName: string,
  min: number,
  max: number
): string | null {
  if (!value.trim()) return `${fieldName} is required`;
  if (value.trim().length < min) return `${fieldName} must be at least ${min} characters`;
  if (value.trim().length > max) return `${fieldName} must be less than ${max} characters`;
  return null;
}

export function validateWordCount(
  value: string,
  fieldName: string,
  min: number,
  max: number
): string | null {
  const words = countWords(value);
  if (words === 0) return `${fieldName} is required`;
  if (words < min) return `${fieldName} must be at least ${min} words (currently: ${words} words)`;
  if (words > max) return `${fieldName} must be at most ${max} words (currently: ${words} words)`;
  return null;
}

export function validatePhone(phone: string): string | null {
  if (!phone.trim()) return "Phone number is required";
  // Accept various phone formats - minimum 7 digits
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.length < 7) return "Please enter a valid phone number";
  return null;
}

export function validateTeamMembers(value: string): string | null {
  if (!value.trim()) return "Please list at least one team member";
  return null;
}

export function validateForm1(data: Form1Data): Partial<Record<keyof Form1Data, string>> {
  const errors: Partial<Record<keyof Form1Data, string>> = {};

  const emailError = validateEmail(data.email);
  if (emailError) errors.email = emailError;

  const nameError = validateTextLength(data.full_name, "Name", 2, 100);
  if (nameError) errors.full_name = nameError;

  const addressError = validateTextLength(data.address, "Address", 10, 200);
  if (addressError) errors.address = addressError;

  const phoneError = validatePhone(data.phone);
  if (phoneError) errors.phone = phoneError;

  const companyError = validateTextLength(data.company_name, "Company name", 2, 100);
  if (companyError) errors.company_name = companyError;

  const problemError = validateWordCount(data.problem_statement, "Problem statement", 10, 50);
  if (problemError) errors.problem_statement = problemError;

  const solutionError = validateWordCount(data.solution_statement, "Solution statement", 10, 50);
  if (solutionError) errors.solution_statement = solutionError;

  const teamError = validateTeamMembers(data.team_members);
  if (teamError) errors.team_members = teamError;

  return errors;
}

// Form 2 validation helpers
export function validateRequired(value: string, fieldName: string): string | null {
  if (!value.trim()) return `Please select ${fieldName}`;
  return null;
}

export function validateProfitMargin(value: number | null): string | null {
  if (value === null || value === undefined) return "Please enter your expected profit margin";
  if (isNaN(value)) return "Profit margin must be a number";
  if (value < 0 || value > 100) return "Profit margin must be between 0 and 100";
  return null;
}

export function validateCompetitors(competitors: string, competitorsOther: string): string | null {
  if (!competitors.trim()) return "Please select a competition option";
  if (competitors === "other" && competitorsOther.trim().length < 10) {
    return "Please describe your competition (at least 10 characters)";
  }
  if (competitors === "other" && competitorsOther.trim().length > 200) {
    return "Competition description must be less than 200 characters";
  }
  return null;
}

export function validateForm2(data: Form2Data): Partial<Record<keyof Form2Data, string>> {
  const errors: Partial<Record<keyof Form2Data, string>> = {};

  const productError = validateWordCount(data.product_description, "Product description", 10, 50);
  if (productError) errors.product_description = productError;

  const localMarketError = validateRequired(data.market_size_local, "your local market size");
  if (localMarketError) errors.market_size_local = localMarketError;

  const regionalMarketError = validateRequired(data.market_size_regional, "your regional market size");
  if (regionalMarketError) errors.market_size_regional = regionalMarketError;

  const competitorsError = validateCompetitors(data.competitors, data.competitors_other);
  if (competitorsError) errors.competitors = competitorsError;

  const diffError = validateWordCount(data.differentiation, "Differentiation", 10, 50);
  if (diffError) errors.differentiation = diffError;

  const salesError = validateRequired(data.sales_projection, "your annual sales projection");
  if (salesError) errors.sales_projection = salesError;

  const marginError = validateProfitMargin(data.profit_margin);
  if (marginError) errors.profit_margin = marginError;

  return errors;
}
